/**
 * CTO Email report (task 035-03) — "Common CTO Leads": one row per traffic
 * source (site) with her columns, a click-in to the source's links with the
 * same columns per link, totals that recompute *Unique* and *New to File*,
 * sortable headers, and the three export buttons per row.
 *
 * Level 1 = `data.sources[]` (README decision 6): the traffic-source label
 * cell is a button that drills into level 2 = `data.links[]` filtered to
 * that source. Level 2's totals row is the SELECTED SOURCE's own row from
 * `sources[]` — never the sum of the link rows (a source's unique is
 * deliberately less than its links' sum). Rate / cost-of-unique-leads follow
 * the mixed / partly-unpriced rule from the pinned contract
 * (`task_02_cto_stats_query_opus.md` §Contract): exactly one rate and no
 * unpriced link ⇒ `rate × source unique`; otherwise the per-link sum with the
 * mixed marker (an unpriced link contributes 0, never a sibling's rate).
 *
 * Talks to the api through the screen-local `cto-stats-api.ts` (A2 (b)) and
 * the shared `api` client for the campaign dropdown only (007's precedent).
 *
 * Task 069-01 adds: the optional Traffic-source Select (D2a — fed by the
 * response's `sources[]`, empty = all) and the By source / By promo grouping
 * toggle (D2b — the promo grain renders its own flat table). The optional
 * `{ range, includeTest }` props are the union tab's controlled seam (D1):
 * present, the "CTO + Paid Social" screen owns the range/test state and this
 * section hides its own filter-bar range/test controls and shell chrome;
 * absent, behavior is exactly 035's.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type CampaignDetailResponse } from '../../lib/api';
import { fmtMoney } from '../../lib/format';
import { Card } from '../../components/ui/card';
import { Select } from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  fetchCtoStats,
  ctoLeadsExportUrl,
  type CtoLeadsVariant,
  type CtoLinkRow,
  type CtoPromoRow,
  type CtoSourceRow,
  type CtoStatsGrouping,
  type CtoStatsResponse,
} from './cto-stats-api';
import { ReportShell } from './report-shell';
import {
  RangeFilters,
  ReportSearch,
  SearchNote,
  TestLeadsToggle,
  matchesSearch,
  useReportRange,
  useSharedReportFilters,
} from './report-filters';
import {
  cell,
  EmptyRow,
  ErrorBanner,
  LoadingRow,
  SortableHeader,
  UNIQUE_TOTAL_TITLE,
  useClientSort,
} from './report-table';

/** The "marked" mixed / partly-unpriced cost cell (task 03 §Scope 2, verbatim). */
const MIXED_COST_TITLE =
  'Mixed or partly unpriced rates — sum of per-link costs; an unpriced link contributes 0 and an address on two links may count twice';

/** Level-1 totals row's `Cost of unique leads` title — that cell IS the sum of the rows above it. */
const LEVEL1_TOTALS_COST_TITLE = 'Adds up the source rows above';

/**
 * Level-2 (link grain) `Cost of unique leads` title, on both the column
 * header and the totals row: the contrast that is on screen — the totals row
 * is the selected source's own cost, not the sum of the link rows below it.
 * The task file does not quote a literal for this one (unlike level-1's,
 * which is quoted); wording chosen to state the same contrast in her
 * language — logged under Decisions made.
 */
const LEVEL2_COST_TITLE =
  "This source's cost across its links — not the sum of the link rows below";

const UNIQUE_COLUMN_HELP = "Distinct addresses across this source's links in the range";
/** The promo grain's Unique header help — the same rule, the promo's links. */
const PROMO_UNIQUE_COLUMN_HELP = "Distinct addresses across this promo's links in the range";
const NOT_CHECKED_COLUMN_HELP =
  "Beehiiv 'The Smart Investor' lookup — new-to-file status, distinct addresses";

/** `NN.NN %`, empty when `null` (uniqueLeads = 0 — the contract's rule). */
function fmtNewToFile(pct: number | null): string {
  if (pct === null) return '';
  return `${pct.toFixed(2)} %`;
}

interface RateCell {
  text: string;
  title?: string;
}

/**
 * The Rate cell rule (task 03 §Scope 2): one rate ⇒ that value through
 * `fmtMoney`; several rates or a partly-unpriced source ⇒ `mixed` with the
 * rates list + "unpriced link(s)" as the title; every link unpriced
 * (`rates` empty) ⇒ empty text with `title="unpriced link(s)"`.
 */
function rateCell(rates: number[], hasUnpricedLink: boolean, mixed: boolean): RateCell {
  if (rates.length === 0) return { text: '', title: 'unpriced link(s)' };
  if (mixed) {
    const parts = rates.map((r) => fmtMoney(r));
    if (hasUnpricedLink) parts.push('unpriced link(s)');
    return { text: 'mixed', title: parts.join(', ') };
  }
  return { text: fmtMoney(rates[0]) };
}

type SourceSortKey =
  | 'siteCode'
  | 'label'
  | 'leads'
  | 'uniqueLeads'
  | 'success'
  | 'duplicate'
  | 'unchecked'
  | 'rejected'
  | 'newToFilePct'
  | 'rate'
  | 'costOfTotal'
  | 'costOfUnique';

/** The Rate sort proxy: the single rate when unambiguous, else `null` (sorts last — mixed/unpriced). */
function sourceAccessor(row: CtoSourceRow, key: SourceSortKey): string | number | null {
  switch (key) {
    case 'siteCode':
      return row.siteCode;
    case 'label':
      return row.label;
    case 'leads':
      return row.leads;
    case 'uniqueLeads':
      return row.uniqueLeads;
    case 'success':
      return row.success;
    case 'duplicate':
      return row.duplicate;
    case 'unchecked':
      return row.unchecked;
    case 'rejected':
      return row.rejected;
    case 'newToFilePct':
      return row.newToFilePct;
    case 'rate':
      return row.rates.length === 1 && !row.costOfUniqueMixed ? row.rates[0] : null;
    case 'costOfTotal':
      return row.costOfTotal;
    case 'costOfUnique':
      return row.costOfUnique;
  }
}

type LinkSortKey =
  | 'campaignName'
  | 'promoTypeName'
  | 'linkStatus'
  | 'leads'
  | 'uniqueLeads'
  | 'success'
  | 'duplicate'
  | 'unchecked'
  | 'rejected'
  | 'newToFilePct'
  | 'price'
  | 'costOfTotal'
  | 'costOfUnique';

function linkAccessor(row: CtoLinkRow, key: LinkSortKey): string | number | null {
  switch (key) {
    case 'campaignName':
      return row.campaignName;
    case 'promoTypeName':
      return row.promoTypeName;
    case 'linkStatus':
      return row.linkStatus;
    case 'leads':
      return row.leads;
    case 'uniqueLeads':
      return row.uniqueLeads;
    case 'success':
      return row.success;
    case 'duplicate':
      return row.duplicate;
    case 'unchecked':
      return row.unchecked;
    case 'rejected':
      return row.rejected;
    case 'newToFilePct':
      return row.newToFilePct;
    case 'price':
      return row.price;
    case 'costOfTotal':
      return row.costOfTotal;
    case 'costOfUnique':
      return row.costOfUnique;
  }
}

type PromoSortKey =
  | 'promoTypeName'
  | 'leads'
  | 'uniqueLeads'
  | 'success'
  | 'duplicate'
  | 'unchecked'
  | 'rejected'
  | 'newToFilePct'
  | 'rate'
  | 'costOfTotal'
  | 'costOfUnique';

function promoAccessor(row: CtoPromoRow, key: PromoSortKey): string | number | null {
  switch (key) {
    case 'promoTypeName':
      return row.promoTypeName;
    case 'leads':
      return row.leads;
    case 'uniqueLeads':
      return row.uniqueLeads;
    case 'success':
      return row.success;
    case 'duplicate':
      return row.duplicate;
    case 'unchecked':
      return row.unchecked;
    case 'rejected':
      return row.rejected;
    case 'newToFilePct':
      return row.newToFilePct;
    case 'rate':
      return row.rates.length === 1 && !row.costOfUniqueMixed ? row.rates[0] : null;
    case 'costOfTotal':
      return row.costOfTotal;
    case 'costOfUnique':
      return row.costOfUnique;
  }
}

/** The three per-row export buttons — `window.open`s the scoped `cto-leads.csv`. */
function ExportButtons({
  idSuffix,
  onExport,
}: {
  idSuffix: string;
  onExport: (variant: CtoLeadsVariant) => void;
}) {
  const labels: readonly [CtoLeadsVariant, string][] = [
    ['unique', 'Unique'],
    ['all', 'All'],
    ['rejected', 'Rejected'],
  ];
  return (
    <div className="flex justify-end gap-1">
      {labels.map(([variant, label]) => (
        <button
          key={variant}
          type="button"
          data-testid={`cto-export-${variant}-${idSuffix}`}
          onClick={() => onExport(variant)}
          className="rounded-md px-1.5 py-0.5 text-xs font-medium text-accent hover:underline"
        >
          {label}
        </button>
      ))}
    </div>
  );
}

/** A source matches by its Traffic Source label or Site ID. */
function matchesSource(q: string, source: CtoSourceRow): boolean {
  return matchesSearch(q, source.label, source.siteCode);
}

/** A link matches by its campaign or promo type name. */
function matchesLink(q: string, link: CtoLinkRow): boolean {
  return matchesSearch(q, link.campaignName, link.promoTypeName);
}

const LEVEL1_COLSPAN = 13;
const LEVEL2_COLSPAN = 14;
const PROMO_COLSPAN = 11;

/**
 * Controlled props (task 069-01, epic 069 D1 — the r1 B-1 / r2 H-1 seam).
 * Absent (every standalone tab): the screen owns its `useReportRange` +
 * test-toggle state and its shell, exactly as before. Present (the union
 * "CTO + Paid Social" tab): the union screen owns the range + test state and
 * passes them down; this section then hides its own filter-bar range/test
 * controls and its `ReportShell`/tab-strip chrome, keeping its section title
 * as a plain label and its own Campaign / Traffic-source selects.
 */
interface CtoStatsProps {
  /** Controlled range — the union screen's shared bar owns it. */
  range?: { from: string; to: string };
  /** Controlled test-leads toggle — the union screen's shared bar owns it. */
  includeTest?: boolean;
}

/** The grouping toggle's two states, with their labels (PresetStrip's idiom). */
const GROUPING_STATES: readonly [CtoStatsGrouping, string][] = [
  ['source', 'By source'],
  ['promo', 'By promo'],
];

export function CtoStats({
  range: controlledRange,
  includeTest: controlledIncludeTest,
}: CtoStatsProps = {}) {
  const navigate = useNavigate();
  const range = useReportRange();
  const controlled = controlledRange !== undefined || controlledIncludeTest !== undefined;
  const from = controlledRange !== undefined ? controlledRange.from : range.from;
  const to = controlledRange !== undefined ? controlledRange.to : range.to;
  const [campaignId, setCampaignId] = useState('');
  // Test leads are shared across the style tabs (URL-backed, 087-01); CTO has no status filter.
  const { includeTest: sharedIncludeTest, setIncludeTest } = useSharedReportFilters();
  // Controlled mode: the union bar's value wins; standalone: the URL-backed shared toggle's.
  const includeTest = controlledIncludeTest ?? sharedIncludeTest;
  const [search, setSearch] = useState('');
  const [siteIdFilter, setSiteIdFilter] = useState('');
  const [grouping, setGrouping] = useState<CtoStatsGrouping>('source');
  const [campaigns, setCampaigns] = useState<CampaignDetailResponse[]>([]);
  const [data, setData] = useState<CtoStatsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);

  // Dropdown loader — the shared typed client (best-effort: an empty dropdown
  // list degrades the filter, not the report).
  useEffect(() => {
    api
      .getCampaigns()
      .then(setCampaigns)
      .catch(() => {
        // Leave the previous (empty) list on error.
      });
  }, []);

  // Fetch on every filter change; the stale response of a superseded filter
  // set is dropped rather than rendered (co-op-counts.tsx's idiom). A default
  // (source-grain, all-sources) request sends no `siteId`/`grouping` param —
  // the API's parse is then byte-identical to before 069-01.
  useEffect(() => {
    if (!from || !to) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchCtoStats({
      from,
      to,
      includeTest,
      campaignId,
      siteId: siteIdFilter || undefined,
      grouping: grouping === 'promo' ? grouping : undefined,
    })
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Request failed');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [from, to, includeTest, campaignId, siteIdFilter, grouping]);

  // A filter change can drop the drilled-into source from the response (or
  // fail the fetch entirely) — fall back to level 1 rather than show a level
  // 2 for a source no longer in range.
  useEffect(() => {
    if (selectedSiteId && data && !data.sources.some((s) => s.siteId === selectedSiteId)) {
      setSelectedSiteId(null);
    }
  }, [data, selectedSiteId]);

  const ctoCampaignOptions = useMemo(
    () => campaigns.filter((c) => c.campaignStyle === 'cto_email'),
    [campaigns]
  );

  // The search narrows the rows only — the footers keep the server's totals (087-01 D5).
  const searchActive = search.trim() !== '';

  // Level 1: a source shows when it matches itself or any of its links does.
  const searchedSources = useMemo(
    () =>
      (data?.sources ?? []).filter(
        (source) =>
          matchesSource(search, source) ||
          (data?.links ?? []).some(
            (link) => link.siteId === source.siteId && matchesLink(search, link)
          )
      ),
    [data, search]
  );

  const {
    sortedRows: sortedSources,
    sort: sourceSort,
    toggleSort: toggleSourceSort,
  } = useClientSort<CtoSourceRow, SourceSortKey>(searchedSources, sourceAccessor);

  const selectedSource = useMemo(
    () =>
      selectedSiteId ? (data?.sources.find((s) => s.siteId === selectedSiteId) ?? null) : null,
    [data, selectedSiteId]
  );

  const allLinksForSource = useMemo(
    () => (selectedSiteId ? (data?.links.filter((l) => l.siteId === selectedSiteId) ?? []) : []),
    [data, selectedSiteId]
  );

  // Level 2: a source that matched itself shows all its links, else only the matching ones.
  const linksForSource = useMemo(
    () =>
      selectedSource === null || matchesSource(search, selectedSource)
        ? allLinksForSource
        : allLinksForSource.filter((link) => matchesLink(search, link)),
    [allLinksForSource, selectedSource, search]
  );

  const {
    sortedRows: sortedLinks,
    sort: linkSort,
    toggleSort: toggleLinkSort,
  } = useClientSort<CtoLinkRow, LinkSortKey>(linksForSource, linkAccessor);

  // The promo grain narrows the same way — a promo shows when its own name matches.
  const searchedPromos = useMemo(
    () => (data?.promos ?? []).filter((promo) => matchesSearch(search, promo.promoTypeName)),
    [data, search]
  );

  const {
    sortedRows: sortedPromos,
    sort: promoSort,
    toggleSort: togglePromoSort,
  } = useClientSort<CtoPromoRow, PromoSortKey>(searchedPromos, promoAccessor);

  const exportSource = (source: CtoSourceRow) => (variant: CtoLeadsVariant) => {
    window.open(
      ctoLeadsExportUrl({
        from,
        to,
        includeTest,
        siteId: source.siteId,
        campaignId: campaignId || undefined,
        variant,
      })
    );
  };

  const exportLink = (link: CtoLinkRow) => (variant: CtoLeadsVariant) => {
    window.open(
      ctoLeadsExportUrl({
        from,
        to,
        includeTest,
        siteId: link.siteId,
        linkId: link.linkId,
        campaignId: campaignId || undefined,
        variant,
      })
    );
  };

  const card = (
    <Card className="overflow-hidden">
      <div className="flex flex-wrap items-center gap-3 border-b border-line px-4 py-3">
        {!controlled && (
          <RangeFilters
            preset={range.preset}
            from={range.from}
            to={range.to}
            applyPreset={range.applyPreset}
            setRange={range.setRange}
            testIdPrefix="cto"
          />
        )}

        <Select
          aria-label="Campaign"
          value={campaignId}
          onChange={(e) => setCampaignId(e.target.value)}
          className="h-9 min-w-[180px]"
        >
          <option value="">All campaigns</option>
          {ctoCampaignOptions.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>

        {/* Task 069-01 D2a — fed by the response's sources[]; empty = all (today's behavior). */}
        <Select
          aria-label="Traffic source"
          data-testid="cto-source-filter"
          value={siteIdFilter}
          onChange={(e) => setSiteIdFilter(e.target.value)}
          className="h-9 min-w-[180px]"
        >
          <option value="">All sources</option>
          {(data?.sources ?? []).map((s) => (
            <option key={s.siteId} value={s.siteId}>
              {`${s.label} (${s.siteCode})`}
            </option>
          ))}
        </Select>

        {/* Task 069-01 D2b — the row grain; 'source' is today's behavior. */}
        <div className="flex gap-1" role="group" aria-label="Group rows by">
          {GROUPING_STATES.map(([value, label]) => (
            <button
              key={value}
              type="button"
              data-testid={`cto-grouping-${value}`}
              onClick={() => setGrouping(value)}
              className={`rounded-md px-2 py-0.5 text-xs font-medium transition ${
                grouping === value
                  ? 'bg-accent text-white'
                  : 'bg-paper text-faint hover:text-ink hairline'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {!controlled && <TestLeadsToggle checked={includeTest} onChange={setIncludeTest} />}

        <ReportSearch
          value={search}
          onChange={setSearch}
          placeholder="Search source, campaign or promo"
          testId="cto-search"
        />

        {loading && <span className="text-xs text-muted-foreground">Loading…</span>}
      </div>

      <SearchNote
        active={searchActive}
        shown={
          grouping === 'promo'
            ? sortedPromos.length
            : selectedSource
              ? sortedLinks.length
              : sortedSources.length
        }
        total={
          grouping === 'promo'
            ? (data?.promos?.length ?? 0)
            : selectedSource
              ? allLinksForSource.length
              : (data?.sources.length ?? 0)
        }
        testId="cto-search-note"
      />

      {error && <ErrorBanner message={error} />}

      {grouping !== 'promo' && selectedSiteId !== null && selectedSource && (
        <div className="flex items-center gap-3 border-b border-line px-4 py-3">
          <button
            type="button"
            data-testid="cto-back"
            onClick={() => setSelectedSiteId(null)}
            className="text-sm font-medium text-accent hover:underline"
          >
            ← All sources
          </button>
          <h2 className="text-sm font-semibold text-ink">{selectedSource.label}</h2>
        </div>
      )}

      <div className="overflow-x-auto">
        {grouping === 'promo' ? (
          /* Task 069-01 D2b — the promo grain's own table (not the drill):
               one row per promo type across the filtered sources; the footer
               is the cross-promo selection total (distinct emails recomputed,
               never summed). No per-row exports — cto-leads.csv scopes by
               source/link, not promo. */
          <Table data-testid="cto-promo-table">
            <TableHeader>
              <TableRow>
                <SortableHeader
                  label="Promo"
                  sortKey="promoTypeName"
                  sort={promoSort}
                  onToggle={togglePromoSort}
                />
                <SortableHeader
                  label="Total leads"
                  sortKey="leads"
                  sort={promoSort}
                  onToggle={togglePromoSort}
                />
                <SortableHeader
                  label="Unique"
                  sortKey="uniqueLeads"
                  sort={promoSort}
                  onToggle={togglePromoSort}
                  title={PROMO_UNIQUE_COLUMN_HELP}
                />
                <SortableHeader
                  label="Success"
                  sortKey="success"
                  sort={promoSort}
                  onToggle={togglePromoSort}
                />
                <SortableHeader
                  label="Duplicate"
                  sortKey="duplicate"
                  sort={promoSort}
                  onToggle={togglePromoSort}
                />
                <SortableHeader
                  label="Not checked"
                  sortKey="unchecked"
                  sort={promoSort}
                  onToggle={togglePromoSort}
                  title={NOT_CHECKED_COLUMN_HELP}
                />
                <SortableHeader
                  label="Rejected"
                  sortKey="rejected"
                  sort={promoSort}
                  onToggle={togglePromoSort}
                />
                <SortableHeader
                  label="New to File"
                  sortKey="newToFilePct"
                  sort={promoSort}
                  onToggle={togglePromoSort}
                />
                <SortableHeader
                  label="Rate"
                  sortKey="rate"
                  sort={promoSort}
                  onToggle={togglePromoSort}
                />
                <SortableHeader
                  label="Cost of total leads"
                  sortKey="costOfTotal"
                  sort={promoSort}
                  onToggle={togglePromoSort}
                />
                <SortableHeader
                  label="Cost of unique leads"
                  sortKey="costOfUnique"
                  sort={promoSort}
                  onToggle={togglePromoSort}
                />
              </TableRow>
            </TableHeader>
            <TableBody>
              {data === null ? (
                <LoadingRow colSpan={PROMO_COLSPAN} />
              ) : sortedPromos.length === 0 ? (
                <EmptyRow
                  colSpan={PROMO_COLSPAN}
                  message={
                    (data.promos?.length ?? 0) > 0 && searchActive
                      ? `No rows match "${search.trim()}".`
                      : 'No leads in this range.'
                  }
                />
              ) : (
                sortedPromos.map((promo) => {
                  const rate = rateCell(
                    promo.rates,
                    promo.hasUnpricedLink,
                    promo.costOfUniqueMixed
                  );
                  return (
                    <TableRow
                      key={promo.promoTypeId}
                      data-testid={`cto-promo-row-${promo.promoTypeId}`}
                    >
                      <TableCell>{promo.promoTypeName}</TableCell>
                      <TableCell className="text-right tabular-nums">{cell(promo.leads)}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {cell(promo.uniqueLeads)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {cell(promo.success)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {cell(promo.duplicate)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {cell(promo.unchecked)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {cell(promo.rejected)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {fmtNewToFile(promo.newToFilePct)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums" title={rate.title}>
                        {rate.text}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {fmtMoney(promo.costOfTotal)}
                      </TableCell>
                      <TableCell
                        className="text-right tabular-nums"
                        title={promo.costOfUniqueMixed ? MIXED_COST_TITLE : undefined}
                      >
                        {fmtMoney(promo.costOfUnique)}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
            {data !== null && (data.promos?.length ?? 0) > 0 && (
              <TableFooter>
                <TableRow data-testid="cto-promo-totals-row">
                  <TableCell>Total</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {cell(data.totals.leads)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums" title={UNIQUE_TOTAL_TITLE}>
                    {cell(data.totals.uniqueLeads)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums" title={UNIQUE_TOTAL_TITLE}>
                    {cell(data.totals.success)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums" title={UNIQUE_TOTAL_TITLE}>
                    {cell(data.totals.duplicate)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums" title={UNIQUE_TOTAL_TITLE}>
                    {cell(data.totals.unchecked)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {cell(data.totals.rejected)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {fmtNewToFile(data.totals.newToFilePct)}
                  </TableCell>
                  <TableCell />
                  <TableCell className="text-right tabular-nums">
                    {fmtMoney(data.totals.costOfTotal)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums" title={LEVEL1_TOTALS_COST_TITLE}>
                    {fmtMoney(data.totals.costOfUnique)}
                  </TableCell>
                </TableRow>
              </TableFooter>
            )}
          </Table>
        ) : selectedSiteId === null || !selectedSource ? (
          <Table data-testid="cto-table">
            <TableHeader>
              <TableRow>
                <SortableHeader
                  label="Site ID"
                  sortKey="siteCode"
                  sort={sourceSort}
                  onToggle={toggleSourceSort}
                />
                <SortableHeader
                  label="Traffic source"
                  sortKey="label"
                  sort={sourceSort}
                  onToggle={toggleSourceSort}
                />
                <SortableHeader
                  label="Total leads"
                  sortKey="leads"
                  sort={sourceSort}
                  onToggle={toggleSourceSort}
                />
                <SortableHeader
                  label="Unique"
                  sortKey="uniqueLeads"
                  sort={sourceSort}
                  onToggle={toggleSourceSort}
                  title={UNIQUE_COLUMN_HELP}
                />
                <SortableHeader
                  label="Success"
                  sortKey="success"
                  sort={sourceSort}
                  onToggle={toggleSourceSort}
                />
                <SortableHeader
                  label="Duplicate"
                  sortKey="duplicate"
                  sort={sourceSort}
                  onToggle={toggleSourceSort}
                />
                <SortableHeader
                  label="Not checked"
                  sortKey="unchecked"
                  sort={sourceSort}
                  onToggle={toggleSourceSort}
                  title={NOT_CHECKED_COLUMN_HELP}
                />
                <SortableHeader
                  label="Rejected"
                  sortKey="rejected"
                  sort={sourceSort}
                  onToggle={toggleSourceSort}
                />
                <SortableHeader
                  label="New to File"
                  sortKey="newToFilePct"
                  sort={sourceSort}
                  onToggle={toggleSourceSort}
                />
                <SortableHeader
                  label="Rate"
                  sortKey="rate"
                  sort={sourceSort}
                  onToggle={toggleSourceSort}
                />
                <SortableHeader
                  label="Cost of total leads"
                  sortKey="costOfTotal"
                  sort={sourceSort}
                  onToggle={toggleSourceSort}
                />
                <SortableHeader
                  label="Cost of unique leads"
                  sortKey="costOfUnique"
                  sort={sourceSort}
                  onToggle={toggleSourceSort}
                />
                <TableHead className="text-right">Exports</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data === null ? (
                <LoadingRow colSpan={LEVEL1_COLSPAN} />
              ) : sortedSources.length === 0 ? (
                <EmptyRow
                  colSpan={LEVEL1_COLSPAN}
                  message={
                    data.sources.length > 0 && searchActive
                      ? `No rows match "${search.trim()}".`
                      : 'No leads in this range.'
                  }
                />
              ) : (
                sortedSources.map((source) => {
                  const rate = rateCell(
                    source.rates,
                    source.hasUnpricedLink,
                    source.costOfUniqueMixed
                  );
                  return (
                    <TableRow key={source.siteId} data-testid={`cto-row-${source.siteId}`}>
                      <TableCell>{source.siteCode}</TableCell>
                      <TableCell>
                        <button
                          type="button"
                          onClick={() => setSelectedSiteId(source.siteId)}
                          className="text-accent hover:underline"
                        >
                          {source.label}
                        </button>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {cell(source.leads)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {cell(source.uniqueLeads)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {cell(source.success)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {cell(source.duplicate)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {cell(source.unchecked)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {cell(source.rejected)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {fmtNewToFile(source.newToFilePct)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums" title={rate.title}>
                        {rate.text}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {fmtMoney(source.costOfTotal)}
                      </TableCell>
                      <TableCell
                        className="text-right tabular-nums"
                        title={source.costOfUniqueMixed ? MIXED_COST_TITLE : undefined}
                      >
                        {fmtMoney(source.costOfUnique)}
                      </TableCell>
                      <TableCell className="text-right">
                        <ExportButtons idSuffix={source.siteId} onExport={exportSource(source)} />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
            {data !== null && data.sources.length > 0 && (
              <TableFooter>
                <TableRow data-testid="cto-totals-row">
                  <TableCell>Total</TableCell>
                  <TableCell />
                  <TableCell className="text-right tabular-nums">
                    {cell(data.totals.leads)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums" title={UNIQUE_TOTAL_TITLE}>
                    {cell(data.totals.uniqueLeads)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums" title={UNIQUE_TOTAL_TITLE}>
                    {cell(data.totals.success)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums" title={UNIQUE_TOTAL_TITLE}>
                    {cell(data.totals.duplicate)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums" title={UNIQUE_TOTAL_TITLE}>
                    {cell(data.totals.unchecked)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {cell(data.totals.rejected)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {fmtNewToFile(data.totals.newToFilePct)}
                  </TableCell>
                  <TableCell />
                  <TableCell className="text-right tabular-nums">
                    {fmtMoney(data.totals.costOfTotal)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums" title={LEVEL1_TOTALS_COST_TITLE}>
                    {fmtMoney(data.totals.costOfUnique)}
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableFooter>
            )}
          </Table>
        ) : (
          <Table data-testid="cto-table">
            <TableHeader>
              <TableRow>
                <SortableHeader
                  label="Campaign"
                  sortKey="campaignName"
                  sort={linkSort}
                  onToggle={toggleLinkSort}
                />
                <SortableHeader
                  label="Promo"
                  sortKey="promoTypeName"
                  sort={linkSort}
                  onToggle={toggleLinkSort}
                />
                <SortableHeader
                  label="Status"
                  sortKey="linkStatus"
                  sort={linkSort}
                  onToggle={toggleLinkSort}
                />
                <SortableHeader
                  label="Total leads"
                  sortKey="leads"
                  sort={linkSort}
                  onToggle={toggleLinkSort}
                />
                <SortableHeader
                  label="Unique"
                  sortKey="uniqueLeads"
                  sort={linkSort}
                  onToggle={toggleLinkSort}
                  title={UNIQUE_TOTAL_TITLE}
                />
                <SortableHeader
                  label="Success"
                  sortKey="success"
                  sort={linkSort}
                  onToggle={toggleLinkSort}
                  title={UNIQUE_TOTAL_TITLE}
                />
                <SortableHeader
                  label="Duplicate"
                  sortKey="duplicate"
                  sort={linkSort}
                  onToggle={toggleLinkSort}
                  title={UNIQUE_TOTAL_TITLE}
                />
                <SortableHeader
                  label="Not checked"
                  sortKey="unchecked"
                  sort={linkSort}
                  onToggle={toggleLinkSort}
                  title={UNIQUE_TOTAL_TITLE}
                />
                <SortableHeader
                  label="Rejected"
                  sortKey="rejected"
                  sort={linkSort}
                  onToggle={toggleLinkSort}
                />
                <SortableHeader
                  label="New to File"
                  sortKey="newToFilePct"
                  sort={linkSort}
                  onToggle={toggleLinkSort}
                />
                <SortableHeader
                  label="Rate"
                  sortKey="price"
                  sort={linkSort}
                  onToggle={toggleLinkSort}
                />
                <SortableHeader
                  label="Cost of total leads"
                  sortKey="costOfTotal"
                  sort={linkSort}
                  onToggle={toggleLinkSort}
                />
                <SortableHeader
                  label="Cost of unique leads"
                  sortKey="costOfUnique"
                  sort={linkSort}
                  onToggle={toggleLinkSort}
                  title={LEVEL2_COST_TITLE}
                />
                <TableHead className="text-right">Exports</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedLinks.length === 0 ? (
                <EmptyRow
                  colSpan={LEVEL2_COLSPAN}
                  message={
                    allLinksForSource.length > 0 && searchActive
                      ? `No rows match "${search.trim()}".`
                      : 'No leads in this range.'
                  }
                />
              ) : (
                sortedLinks.map((link) => (
                  <TableRow key={link.linkId} data-testid={`cto-link-row-${link.linkId}`}>
                    <TableCell>
                      <button
                        type="button"
                        onClick={() => navigate(`/campaigns/${link.campaignId}`)}
                        className="text-accent hover:underline"
                      >
                        {link.campaignName}
                      </button>
                    </TableCell>
                    <TableCell>{link.promoTypeName}</TableCell>
                    <TableCell>{link.linkStatus}</TableCell>
                    <TableCell className="text-right tabular-nums">{cell(link.leads)}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {cell(link.uniqueLeads)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{cell(link.success)}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {cell(link.duplicate)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {cell(link.unchecked)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{cell(link.rejected)}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {fmtNewToFile(link.newToFilePct)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {fmtMoney(link.price)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {fmtMoney(link.costOfTotal)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {fmtMoney(link.costOfUnique)}
                    </TableCell>
                    <TableCell className="text-right">
                      <ExportButtons idSuffix={link.linkId} onExport={exportLink(link)} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
            {allLinksForSource.length > 0 &&
              (() => {
                const rate = rateCell(
                  selectedSource.rates,
                  selectedSource.hasUnpricedLink,
                  selectedSource.costOfUniqueMixed
                );
                return (
                  <TableFooter>
                    <TableRow data-testid="cto-totals-row">
                      <TableCell>Total</TableCell>
                      <TableCell />
                      <TableCell />
                      <TableCell className="text-right tabular-nums">
                        {cell(selectedSource.leads)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums" title={UNIQUE_TOTAL_TITLE}>
                        {cell(selectedSource.uniqueLeads)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums" title={UNIQUE_TOTAL_TITLE}>
                        {cell(selectedSource.success)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums" title={UNIQUE_TOTAL_TITLE}>
                        {cell(selectedSource.duplicate)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums" title={UNIQUE_TOTAL_TITLE}>
                        {cell(selectedSource.unchecked)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {cell(selectedSource.rejected)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {fmtNewToFile(selectedSource.newToFilePct)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums" title={rate.title}>
                        {rate.text}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {fmtMoney(selectedSource.costOfTotal)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums" title={LEVEL2_COST_TITLE}>
                        {fmtMoney(selectedSource.costOfUnique)}
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  </TableFooter>
                );
              })()}
          </Table>
        )}
      </div>
    </Card>
  );

  if (controlled) {
    // Union-tab mode (task 069-01 D1, the r2 H-1 fold): the shared bar above
    // the two sections owns the range and the test toggle, so this section's
    // shell chrome is suppressed — the section title stays as a plain label.
    return (
      <section data-testid="cto-section">
        <h2 className="text-sm font-semibold text-ink">CTO Email</h2>
        {card}
      </section>
    );
  }

  return (
    <ReportShell
      title="CTO Email"
      desc="Leads by traffic source — unique across a source's links; cost per link rate, and a source with mixed or unpriced links shows the per-link sum. Test leads hidden by default."
    >
      {card}
    </ReportShell>
  );
}
